import React, { useState } from 'react';
import axios from 'axios';
import { Download, Mail, Pencil, Printer, Smartphone } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { FormFillInFields } from './FormFillInFields';
import { useToast } from '../hooks/use-toast';

const backendUrl = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('dentalToken')}` } });

const fetchPdfBlob = async (consent) => {
  const params = new URLSearchParams({
    patient_name: consent.patientName || '',
    dentist_name: consent.dentistName || '',
    date: new Date(consent.assignedDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
    treatment_refused: consent.treatmentRefused || '',
    assignment_id: consent.id || '',
  });
  const res = await axios.get(`${backendUrl}/api/informed-consent/view-pdf/${consent.consentId}?${params}`, {
    ...auth(),
    responseType: 'blob',
  });
  return URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
};

const EditFieldsDialog = ({ consent, open, onOpenChange, onSaved }) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [values, setValues] = useState(null);
  const [saving, setSaving] = useState(false);
  const signed = consent.status === 'signed' || consent.status === 'signed_in_office';

  React.useEffect(() => {
    if (!open) return;
    axios
      .get(`${backendUrl}/api/informed-consent/assignment-fields/${consent.id}`, auth())
      .then((r) => setValues(r.data?.data?.fieldValues || {}))
      .catch(() => setValues({}));
  }, [open, consent.id]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${backendUrl}/api/informed-consent/assignment-fields/${consent.id}`, { fieldValues: values }, auth());
      toast({ title: 'Saved', description: "The patient's copy has been updated." });
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast({ title: 'Could not save', description: e.response?.data?.detail || 'Unknown error', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid={`compliance-edit-dialog-${consent.id}`}>
        <DialogHeader>
          <DialogTitle>Edit — {consent.consentName}</DialogTitle>
        </DialogHeader>
        {signed ? (
          <p className="text-sm text-gray-600">This form has been signed, so the patient's copy can no longer be changed.</p>
        ) : (
          values && <FormFillInFields consentId={consent.consentId} values={values} onChange={setValues} />
        )}
        <p className="text-xs text-gray-500">
          To change the wording for every patient, edit the practice template instead.
        </p>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => navigate(`/edit-informed-consent/${consent.consentId}`)} data-testid={`compliance-edit-template-${consent.id}`}>
            Edit Practice Template
          </Button>
          {!signed && (
            <Button onClick={save} disabled={saving || !values} className="bg-amber-600 hover:bg-amber-700" data-testid={`compliance-edit-save-${consent.id}`}>
              {saving ? 'Saving…' : "Save Patient's Copy"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const AssignedFormActions = ({ consent, patient, onRefresh, onText }) => {
  const { toast } = useToast();
  const [editOpen, setEditOpen] = useState(false);
  const fail = (title, e) => toast({ title, description: e?.response?.data?.detail || 'Please try again', variant: 'destructive' });

  const print = async () => {
    try {
      const url = await fetchPdfBlob(consent);
      const iframe = document.createElement('iframe');
      Object.assign(iframe.style, { position: 'fixed', right: 0, bottom: 0, width: 0, height: 0, border: 'none' });
      iframe.src = url;
      iframe.onload = () => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
        setTimeout(() => { iframe.remove(); URL.revokeObjectURL(url); }, 120000);
      };
      document.body.appendChild(iframe);
    } catch (e) { fail('Print failed', e); }
  };

  const download = async () => {
    try {
      const url = await fetchPdfBlob(consent);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(consent.consentName || 'Compliance_Form').replace(/[^\w-]+/g, '_')}_${(consent.patientName || '').replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { fail('Download failed', e); }
  };

  const email = async () => {
    const to = patient?.email || consent.patientEmail;
    if (!to) return toast({ title: 'No Email Address', description: 'This patient does not have an email address on file', variant: 'destructive' });
    try {
      await axios.post(`${backendUrl}/api/informed-consent/email`, {
        patientEmail: to,
        patientName: consent.patientName || `${patient?.firstName || ''} ${patient?.lastName || ''}`.trim(),
        consentId: consent.consentId,
        consentName: consent.consentName,
        assignmentId: consent.id,
      }, auth());
      toast({ title: 'Email Sent', description: `${consent.consentName} sent to ${to}` });
      onRefresh?.();
    } catch (e) { fail('Email failed', e); }
  };

  const btn = 'text-sm px-3 py-1 h-8 bg-white';
  return (
    <div className="flex flex-wrap gap-2 pt-3 border-t border-black/5" data-testid={`compliance-actions-${consent.id}`}>
      <Button size="sm" variant="outline" className={btn} onClick={() => setEditOpen(true)} data-testid={`compliance-edit-btn-${consent.id}`}>
        <Pencil className="h-3 w-3 mr-1" /> Edit
      </Button>
      <Button size="sm" variant="outline" className={btn} onClick={print} data-testid={`compliance-print-btn-${consent.id}`}>
        <Printer className="h-3 w-3 mr-1" /> Print
      </Button>
      <Button size="sm" variant="outline" className={btn} onClick={email} data-testid={`compliance-email-btn-${consent.id}`}>
        <Mail className="h-3 w-3 mr-1" /> Email
      </Button>
      <Button size="sm" variant="outline" className={btn} onClick={download} data-testid={`compliance-download-btn-${consent.id}`}>
        <Download className="h-3 w-3 mr-1" /> Download
      </Button>
      <Button size="sm" variant="outline" className={btn} onClick={onText} data-testid={`compliance-text-btn-${consent.id}`}>
        <Smartphone className="h-3 w-3 mr-1" /> Text
      </Button>
      <EditFieldsDialog consent={consent} open={editOpen} onOpenChange={setEditOpen} onSaved={onRefresh} />
    </div>
  );
};

export default AssignedFormActions;
